import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import React, { useRef, useMemo } from 'react';

import styles from './Table.module.scss';
import TableBody from './TableBody';

const cx = classNames.bind(styles);

const Table = ({ data }) => {
  const tableContainerRef = useRef();
  const columnHelper = createColumnHelper();

  const isEmpty = useMemo(() => {
    return data.length === 0;
  }, [data]);

  const columns = useMemo(() => {
    if (isEmpty) return [];

    return Object.keys(data[0]).map((key) => {
      // 컬럼명 길이
      const nameLength = key.length;
      // 데이터 길이
      const valueLength = String(data[0][key]).length;
      // 길이가 더 긴 값을 기준으로 너비 추산 (글자수 * 8px)
      const size = Math.max(nameLength, valueLength) * 8;

      return columnHelper.accessor(key, {
        id: key,
        header: key,
        accessorKey: key,
        size,
        cell: (info) => {
          const value = info.getValue();

          switch (key) {
            case 'time':
              return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
            default:
              return value;
          }
        },
      });
    });
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: { sorting: [{ id: 'time', desc: true }] },
  });

  return (
    <div ref={tableContainerRef} className={cx(['container', { hide: isEmpty }])}>
      <div className={cx('wrapper')}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} style={{ width: header.getSize() }}>
                    <div
                      {...{
                        className: header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                        onClick: header.column.getToggleSortingHandler(),
                      }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                      }[header.column.getIsSorted()] ?? null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <TableBody table={table} tableContainerRef={tableContainerRef} />
        </table>
      </div>
    </div>
  );
};

export default Table;
